import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { IoClose } from 'react-icons/io5'
import { FiCheck } from 'react-icons/fi'
import { ErosLogoSVG } from '../../assets'
import ShimmerEffect from '../ShimmerEffect/ShimmerEffect'
import {
  acceptConsent,
  closeConsentModal,
  openConsentModal
} from '../../redux/reducers/consentReducer'
import {
  MINI_APP_IDS,
  CONSENT_CONFIG,
  getAppScope,
  isScopeMatch
} from '../../utils/constants/config'
import { ROUTES } from '../../utils/constants'
import {
  useSaveConsent,
  useAvailableConsents,
  useConsentDetails
} from '../../hooks/useConsent'
import { useToast } from '../UI/ToastProvider'
import { useTrackEvent } from 'shared-ui/utils/analyticEvents'
import { GA_EVENTS } from 'shared-ui/utils/gaConstants'
import { getGuestUser } from '../../utils/localStorage'
import { isPreviewMode } from '../../utils/previewMode'

const MiniAppConsentModal = ({
  isOpen: propIsOpen,
  miniAppId: propMiniAppId,
  isGuestUser: propIsGuestUser,
  guestUser: propGuestUser,
  onClose: propOnClose,
  onAgreed: propOnAgreed,
  onDeclined: propOnDeclined
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { showError } = useToast()
  const reduxModal = useSelector((state) => state.consent?.modal)
  const trackEvent = useTrackEvent()

  const currentPath = (location?.pathname || '').toLowerCase().replace(/\/+$/, '')
  const privacyPath = (ROUTES?.privacy || '/privacy').toLowerCase().replace(/\/+$/, '')
  const termsPath = (ROUTES?.terms || '/terms').toLowerCase().replace(/\/+$/, '')

  const isLegalPage =
    currentPath === privacyPath ||
    currentPath === termsPath ||
    currentPath.startsWith(`${privacyPath}/`) ||
    currentPath.startsWith(`${termsPath}/`)

  const isAuthenticated =
    useSelector((state) => state.auth?.isAuthenticated) ||
    !!localStorage.getItem('access')
  const guestUserValue =
    propIsGuestUser !== undefined
      ? propIsGuestUser
      : propGuestUser !== undefined
      ? propGuestUser
      : getGuestUser()
  const isGuestUser =
    !isAuthenticated &&
    (guestUserValue === true || guestUserValue === 'true')

  const createConsent = useSelector(
    (state) => state.consent?.miniApps?.[MINI_APP_IDS.CREATE]
  )
  const homePath = (ROUTES?.home || '/home').toLowerCase().replace(/\/+$/, '')
  const createPath = (ROUTES?.create || '/create').toLowerCase().replace(/\/+$/, '')
  const aiToolPath = '/ai-tool/'
  const isCreateHome = currentPath === homePath || currentPath === createPath || currentPath.startsWith(aiToolPath)

  const isPreview = isPreviewMode()

  const hasDeclinedForCurrentPath = useRef(false)

  // Reset decline flag when route changes away from Create pages
  useEffect(() => {
    if (!isCreateHome) {
      hasDeclinedForCurrentPath.current = false
    }
  }, [isCreateHome, currentPath])

  // If user navigates to Create Home or Create page without consent, prompt consent modal
  useEffect(() => {
    if (!isPreview && !isGuestUser && !isLegalPage && isCreateHome) {
      const hasCreateConsent = !!(createConsent?.hasAgreed && createConsent?.isOn)
      if (!hasCreateConsent && !reduxModal?.isOpen && !hasDeclinedForCurrentPath.current) {
        dispatch(
          openConsentModal({
            miniAppId: MINI_APP_IDS.CREATE,
            isModalBlocking: true
          })
        )
      }
    }
  }, [
    isPreview,
    isCreateHome,
    isGuestUser,
    isLegalPage,
    createConsent?.hasAgreed,
    createConsent?.isOn,
    reduxModal?.isOpen,
    dispatch
  ])

  // If modal is open on Create page and server syncs that consent is already agreed, auto-close modal
  useEffect(() => {
    if (
      reduxModal?.isOpen &&
      !isGuestUser &&
      reduxModal?.actionTriggered !== 'toggle' &&
      reduxModal?.actionTriggered !== 'optional_toggle'
    ) {
      if (
        (reduxModal?.miniAppId || MINI_APP_IDS.CREATE) === MINI_APP_IDS.CREATE &&
        createConsent?.hasAgreed &&
        createConsent?.isOn
      ) {
        dispatch(closeConsentModal())
        if (reduxModal?.redirectUrl) {
          if (
            reduxModal.redirectUrl.startsWith('/') ||
            !reduxModal.redirectUrl.startsWith('http')
          ) {
            navigate(reduxModal.redirectUrl)
          } else {
            window.location.href = reduxModal.redirectUrl
          }
        }
      }
    }
  }, [
    reduxModal?.isOpen,
    reduxModal?.miniAppId,
    reduxModal?.actionTriggered,
    reduxModal?.redirectUrl,
    isGuestUser,
    createConsent?.hasAgreed,
    createConsent?.isOn,
    dispatch,
    navigate
  ])

  // If Redux modal is open but user is a guest, close it in Redux and navigate if redirectUrl is present
  useEffect(() => {
    if (reduxModal?.isOpen && isGuestUser) {
      const redirectUrl = reduxModal?.redirectUrl
      dispatch(closeConsentModal())
      if (redirectUrl) {
        if (redirectUrl.startsWith('/') || !redirectUrl.startsWith('http')) {
          navigate(redirectUrl)
        } else {
          window.location.href = redirectUrl
        }
      }
    }
  }, [reduxModal?.isOpen, reduxModal?.redirectUrl, isGuestUser, dispatch, navigate])

  // Use props if provided, otherwise fallback to Redux store
  const isOpen =
    !isPreview && (propIsOpen !== undefined ? propIsOpen : (reduxModal?.isOpen || false))
  const miniAppId = propMiniAppId || reduxModal?.miniAppId || MINI_APP_IDS.CREATE

  const { mutateAsync: saveConsent } = useSaveConsent()
  const { data: availableData, isLoading: isAvailableLoading } = useAvailableConsents(
    {},
    { enabled: !!isOpen && !isLegalPage && !isGuestUser }
  )

  const dynamicScope = getAppScope(miniAppId, availableData)
  const isWellness =
    miniAppId === MINI_APP_IDS.WELLNESS ||
    isScopeMatch(dynamicScope, MINI_APP_IDS.WELLNESS)

  // Fetch dynamic consent details from API
  const {
    data: detailsData,
    isLoading: isDetailsLoading,
    isFetching: isDetailsFetching,
    isError: isDetailsError,
    error: detailsError
  } = useConsentDetails(dynamicScope, {
    enabled: !!isOpen && !isLegalPage && !isGuestUser && !!dynamicScope
  })
  const details = detailsData?.data || detailsData || null

  // Loading state until API succeeds
  const isConsentLoading =
    !details && (isDetailsLoading || isDetailsFetching || isAvailableLoading || !detailsData)

  const title = details?.title || dynamicScope || ''
  const subtitle = details?.subtitle || ''
  const consentVersion = details?.version || '1.0'

  // Sections from API only - no hardcoded fallback data
  const rawSections =
    Array.isArray(details?.sections) && details.sections.length > 0
      ? [...details.sections].sort((a, b) => (a.order || 0) - (b.order || 0))
      : []

  const isWithdrawSection = (sec) =>
    typeof sec?.title === 'string' && /withdraw/i.test(sec.title.trim())

  const withdrawSection = rawSections.find(isWithdrawSection)
  const withdrawContent = withdrawSection?.content || ''

  const sections = rawSections.filter((sec) => !isWithdrawSection(sec))

  // Checkboxes from API only
  const availableItem = availableData?.results?.find((item) =>
    isScopeMatch(item.scope, miniAppId)
  )
  const optionalConsentText = availableItem?.description || ''

  const apiCheckboxes = Array.isArray(details?.checkboxes) ? details.checkboxes : []
  const requiredCheckboxItem = apiCheckboxes.find(
    (cb) => cb.is_required || cb.consent_type?.toLowerCase() === 'required'
  )
  const requiredLabel = requiredCheckboxItem?.label || ''

  const optionalCheckboxItem = apiCheckboxes.find(
    (cb) => !cb.is_required || cb.consent_type?.toLowerCase() === 'optional'
  )
  const optionalLabel = optionalCheckboxItem?.label || optionalConsentText || ''

  const hasOptionalCheckbox =
    !isWellness && (!!optionalCheckboxItem || !!optionalConsentText)

  const [requiredChecked, setRequiredChecked] = useState(false)
  const [optionalChecked, setOptionalChecked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Scroll tracking for Eros Wellness
  const contentScrollRef = useRef(null)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const maxScroll = scrollHeight - clientHeight
    if (maxScroll <= 0) {
      setScrollProgress(100)
      setHasScrolledToBottom(true)
      return
    }
    const currentProgress = Math.min(
      100,
      Math.max(0, Math.round((scrollTop / maxScroll) * 100))
    )
    setScrollProgress(currentProgress)
    if (currentProgress >= 90 || scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToBottom(true)
    }
  }
  useEffect(() => {
    const consentData = availableItem?.history_data ?? []
    const consentType = consentData?.length === 0 ? 'initial ' : 'reconsent'
    const oldversion = consentData?.length
      ? availableItem.history_data[0]?.consent_version
      : ''
    if (isOpen) {
      trackEvent(GA_EVENTS.consent_screen_open, {
        mini_app: miniAppId,
        consent_version: consentVersion,
        consent_flow:consentType,
        timestamp: new Date().toISOString()
      })
      if(consentVersion !== oldversion){
        trackEvent(GA_EVENTS.consent_reconsent_required, {
        mini_app: miniAppId,
        new_consent_version: consentVersion,
        consent_flow:consentType,
        old_consent_version:oldversion,
        timestamp: new Date().toISOString(),
        change_reason:"other_material_change"
      })
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (requiredChecked) {
      trackEvent(GA_EVENTS.consent_checkbox_selected, {
        mini_app: miniAppId,
        consent_version: consentVersion,
        timestamp: new Date().toISOString()
      })
    }
  }, [requiredChecked])
  // Reset state when opening modal
  useEffect(() => {
    if (isOpen && !isGuestUser) {
      setRequiredChecked(false)
      const shouldCheckOptional =
        reduxModal?.defaultOptionalChecked ||
        reduxModal?.actionTriggered === 'optional_toggle'
      setOptionalChecked(!!shouldCheckOptional)
      setIsSubmitting(false)

      if (isWellness) {
        setScrollProgress(0)
        setHasScrolledToBottom(false)
        const timer = setTimeout(() => {
          if (contentScrollRef.current) {
            const el = contentScrollRef.current
            if (el.scrollHeight <= el.clientHeight) {
              setScrollProgress(100)
              setHasScrolledToBottom(true)
            } else {
              el.scrollTop = 0
            }
          }
        }, 120)
        return () => clearTimeout(timer)
      }
    }
  }, [isOpen, miniAppId, isWellness, detailsData, isGuestUser, reduxModal])

  if (!isOpen || isLegalPage || isGuestUser) return null

  const handleClose = () => {
    trackEvent(GA_EVENTS.consent_declined, {
      mini_app: miniAppId,
      consent_version: consentVersion,
      consent_declined: "closed",
      timestamp: new Date().toISOString()
    })
    if (propOnDeclined) {
      propOnDeclined()
    }

    if (propOnClose) {
      propOnClose()
    } else {
      dispatch(closeConsentModal())
    }

    if (isCreateHome && !isLegalPage && !isGuestUser) {
      hasDeclinedForCurrentPath.current = true
      navigate(ROUTES.sAppHome || '/super-app-home', { replace: true })
    }
  }

  const isAgreeDisabled =
    isConsentLoading ||
    (isWellness
      ? !hasScrolledToBottom || !requiredChecked || isSubmitting
      : !requiredChecked || isSubmitting)

  const handleAgreeAndContinue = async () => {
    if (isAgreeDisabled) return
    setIsSubmitting(true)

    try {
      // 1. Save mandatory consent using dynamic scope from API (e.g. "Eros Create", "Eros Wellness")
      const mandatoryRes = await saveConsent({
        scope: dynamicScope,
        consent_type: 'MANDATORY',
        consent_version: consentVersion,
        platform: 'web'
      })
      const savedMandatoryId =
        mandatoryRes?.data?.consent_id || mandatoryRes?.consent_id || null

      let savedOptionalId = null
      const consentData= availableItem?.history_data
      const action = consentData?.find(
        (item) => item.consent_id === savedMandatoryId
      )?.action ?? ''
      const oldVerison = consentData?.find(
        (item) => item.consent_id === savedMandatoryId
      )?.consent_version ?? ''
      
      if (action === 'REVOKED') {
        trackEvent(GA_EVENTS.consent_reaccepted, {
          mini_app: miniAppId,
          new_consent_version: consentVersion,
          old_consent_version:oldVerison,
          consent_status: mandatoryRes?.data?.status?.toLowerCase() ?? '',
          timestamp: new Date().toISOString()
        })
      } else {
        trackEvent(GA_EVENTS.consent_accepted, {
          mini_app: miniAppId,
          consent_version: consentVersion,
          consent_status: mandatoryRes?.data?.status?.toLowerCase() ?? '',
          timestamp: new Date().toISOString()
        })
      }

      // 2. If optional checkbox is checked (Eros Create), save optional consent
      if (!isWellness && optionalChecked) {
        try {
          const optionalRes = await saveConsent({
            scope: dynamicScope,
            consent_type: 'OPTIONAL',
            consent_version: consentVersion,
            platform: 'web'
          })
          savedOptionalId =
            optionalRes?.data?.consent_id || optionalRes?.consent_id || null

          const newAction = consentData?.find(
            (item) => item?.consent_id === savedOptionalId
          )?.action ?? ''
          const oldDataVerison = consentData?.find(
            (item) => item?.consent_id === savedOptionalId
          )?.consent_version ?? ''

          if (newAction === 'REVOKED') {
            trackEvent(GA_EVENTS.consent_reaccepted, {
              mini_app: miniAppId,
              new_consent_version: consentVersion,
              old_consent_version: oldDataVerison,
              consent_status: mandatoryRes?.data?.status?.toLowerCase() ?? '',
              timestamp: new Date().toISOString()
            })
          } else {
            trackEvent(GA_EVENTS.consent_accepted, {
              mini_app: miniAppId,
              consent_version: consentVersion,
              consent_status: mandatoryRes?.data?.status?.toLowerCase() ?? '',
              timestamp: new Date().toISOString()
            })
          }
        } catch(err) {
          trackEvent(GA_EVENTS.consent_error, {
            mini_app: miniAppId,
            error_category: err?.message,
            timestamp: new Date().toISOString()
          })
          // Silent catch for non-blocking optional consent save
        }
      }

      // 3. Dispatch acceptConsent to Redux
      dispatch(
        acceptConsent({
          miniAppId,
          optionalChecked: !isWellness && optionalChecked,
          consentId: savedMandatoryId,
          optionalConsentId: savedOptionalId,
          version: consentVersion
        })
      )

      if (propOnAgreed) {
        propOnAgreed({
          optionalChecked: !isWellness && optionalChecked,
          consentId: savedMandatoryId,
          optionalConsentId: savedOptionalId
        })
      }

      if (reduxModal?.redirectUrl) {
        dispatch(closeConsentModal())
        if (reduxModal.redirectUrl.startsWith('/') || !reduxModal.redirectUrl.startsWith('http')) {
          navigate(reduxModal.redirectUrl)
        } else {
          window.location.href = reduxModal.redirectUrl
        }
        return
      }

      if (propOnClose) {
        propOnClose()
      } else {
        dispatch(closeConsentModal())
      }
    } catch (err) {
       trackEvent(GA_EVENTS.consent_error, {
          mini_app: miniAppId,
          error_category: err?.message,
          timestamp: new Date().toISOString()
        })
      showError(err?.message || CONSENT_CONFIG.FAILED_SAVE_CONSENT)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePrivacyClick = () => {
    navigate(ROUTES.privacy, {
      state: { prevLocation: location.pathname }
    })
  }

  const handleTermsClick = () => {
    navigate(ROUTES.terms, {
      state: { prevLocation: location.pathname }
    })
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end md:items-center justify-center z-[9999] p-0 md:p-4 animate-fade-in"
      onClick={handleClose}
    >
      <style>{`
        .consent-modal-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .consent-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .consent-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 9999px;
        }
        .consent-modal-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.35);
        }
      `}</style>

      {/* Shared Outer Modal Container: Same width (max-w-[620px]), height, padding & radius for both Create and Wellness */}
      <div
        className="bg-[#0C111D] shadow-[0px_12px_24px_0px_#00000059] text-left border-t md:border border-[#FFFFFF1A] rounded-t-[20px] md:rounded-[20px] rounded-b-none md:rounded-b-[20px] w-full max-w-full md:max-w-[620px] max-h-[92vh] md:max-h-[90vh] overflow-y-auto p-4 sm:p-5 md:p-6 flex flex-col gap-4 text-white relative consent-modal-scroll"
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile top pull-down handle */}
        <div className="w-12 h-1 bg-white/20 rounded-full mx-auto -mt-0.5 mb-1.5 md:hidden shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between shrink-0">
          {isWellness ? (
            <div className="flex flex-col select-none">
              <div className="h-[18px] sm:h-[20px] overflow-hidden flex items-start">
                <img
                  src={ErosLogoSVG}
                  alt="Eros"
                  className="h-[30px] sm:h-[34px] w-auto max-w-none object-cover object-top"
                />
              </div>
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.22em] text-[#00B8F8] uppercase leading-none mt-0.5">
                Wellness
              </span>
            </div>
          ) : (
            <img
              src={ErosLogoSVG}
              alt="Eros Universe"
              className="h-5 sm:h-6 md:h-6 w-auto object-contain"
            />
          )}

          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="text-[#94A3B8] hover:text-white bg-[#FFFFFF0F] hover:bg-white/10 rounded-2xl flex items-center justify-center transition-colors cursor-pointer border-0 p-1 shrink-0"
          >
            <IoClose size={20} />
          </button>
        </div>

        {/* Title and Subtitle */}
        {isDetailsError && !details ? (
          <div className="shrink-0">
            <h2 className="text-xl font-bold text-white leading-tight m-0">
              {title || 'Eros Universe'}
            </h2>
            <p className="text-xs leading-relaxed text-[#EF4444] mt-1 mb-0">
              {detailsError?.message || 'Unable to load consent details. Please check your connection and try again.'}
            </p>
          </div>
        ) : isConsentLoading ? (
          <div className="shrink-0 flex flex-col gap-2">
            <ShimmerEffect width="170px" height="24px" radius="6px" />
            <ShimmerEffect width="92%" height="13px" radius="4px" />
            <ShimmerEffect width="72%" height="13px" radius="4px" />
          </div>
        ) : (
          <div className="shrink-0">
            <h2 className="text-xl font-bold text-white leading-tight m-0">
              {title}
            </h2>
            <p className="text-xs leading-relaxed text-[#98A2B3] mt-1 mb-0">
              {subtitle}
            </p>
          </div>
        )}

        {/* Content Card */}
        {isDetailsError && !details ? null : isConsentLoading ? (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-3.5 sm:p-4 flex flex-col gap-3.5 shrink-0">
            <div className="flex flex-col gap-1.5">
              <ShimmerEffect width="110px" height="15px" radius="4px" />
              <ShimmerEffect width="96%" height="12px" radius="4px" />
              <ShimmerEffect width="80%" height="12px" radius="4px" />
            </div>
            <div className="flex flex-col gap-1.5">
              <ShimmerEffect width="65px" height="15px" radius="4px" />
              <ShimmerEffect width="88%" height="12px" radius="4px" />
            </div>
            <div className="flex flex-col gap-1.5">
              <ShimmerEffect width="85px" height="15px" radius="4px" />
              <ShimmerEffect width="94%" height="12px" radius="4px" />
            </div>
            <div className="flex flex-col gap-1.5">
              <ShimmerEffect width="95px" height="15px" radius="4px" />
              <ShimmerEffect width="78%" height="12px" radius="4px" />
            </div>
          </div>
        ) : isWellness ? (
          /* Wellness Content Card with dynamic sections & scroll tracking */
          <div
            ref={contentScrollRef}
            onScroll={handleScroll}
            className="bg-white/[0.02] border border-white/10 rounded-2xl p-3.5 sm:p-4 max-h-[285px] sm:max-h-[305px] overflow-y-auto flex flex-col gap-3 sm:gap-3.5 text-left consent-modal-scroll shrink-0"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent' }}
          >
            {sections.map((sec, idx) => (
              <div key={sec.title || idx} className="flex flex-col gap-1">
                <div className="text-sm font-semibold text-white">
                  {sec.title}
                </div>
                <div className="text-xs leading-relaxed text-[#98A2B3] whitespace-pre-line">
                  {sec.content}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Create Info Card */
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-3.5 sm:p-4 flex flex-col gap-2.5 sm:gap-3 shrink-0">
            {sections.map((sec, idx) => (
              <div key={sec.title || idx} className="flex flex-col gap-0.5">
                <div className="text-sm font-semibold text-white">{sec.title}</div>
                <div className="text-xs leading-relaxed text-[#98A2B3] whitespace-pre-line">
                  {sec.content}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Scroll Progress Bar Row (Wellness only) */}
        {!isConsentLoading && isWellness && (
          <div className="flex items-center gap-3 text-xs text-[#98A2B3] shrink-0 pt-0.5">
            <span className="shrink-0 text-xs text-[#98A2B3] font-medium">
              {CONSENT_CONFIG.KEEP_SCROLLING_HINT}
            </span>
            <div className="w-[55%] h-1 bg-[#1E293B] rounded-sm overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#63E6A2] to-[#00B8F8] rounded-full transition-all duration-150"
                style={{ width: `${Math.max(scrollProgress, hasScrolledToBottom ? 100 : 8)}%` }}
              />
            </div>
          </div>
        )}

        {/* Privacy & Terms (Both) */}
        <div className="flex items-center justify-center gap-2 text-xs shrink-0 py-0.5">
          <button
            type="button"
            onClick={handlePrivacyClick}
            className="text-[#3EE08A] hover:underline font-semibold cursor-pointer bg-transparent border-0 p-0"
          >
            {/* {SUPER_APP_CONFIG?.PRIVACY_POLICY || 'Privacy Policy'} */}
            {'Privacy Policy'}
          </button>
          <span className="text-slate-500">·</span>
          <button
            type="button"
            onClick={handleTermsClick}
            className="text-[#3EE08A] hover:underline font-semibold cursor-pointer bg-transparent border-0 p-0"
          >
            {/* {SUPER_APP_CONFIG?.TERMS_AND_CONDITIONS || 'Terms of Service'} */}
            {'Terms of Service'}
          </button>
        </div>

        {/* Checkbox 1: Required */}
        {isDetailsError && !details ? null : isConsentLoading ? (
          <>
            <div className="rounded-2xl p-3 sm:p-3.5 md:p-4 flex items-start gap-3 border border-white/10 bg-white/[0.02] shrink-0">
              <ShimmerEffect width="20px" height="20px" radius="4px" className="shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1.5 flex-1">
                <ShimmerEffect width="92%" height="13px" radius="4px" />
                <ShimmerEffect width="55px" height="11px" radius="4px" />
              </div>
            </div>
            {!isWellness && (
              <div className="rounded-2xl p-3 sm:p-3.5 md:p-4 flex items-start gap-3 border border-white/10 bg-white/[0.02] shrink-0">
                <ShimmerEffect width="20px" height="20px" radius="4px" className="shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1.5 flex-1">
                  <ShimmerEffect width="88%" height="13px" radius="4px" />
                  <ShimmerEffect width="55px" height="11px" radius="4px" />
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div
              onClick={() => setRequiredChecked(!requiredChecked)}
              className={`rounded-2xl p-3 sm:p-3.5 md:p-4 flex ${
                isWellness ? 'items-center' : 'items-start'
              } gap-3 cursor-pointer transition-all border shrink-0 ${
                requiredChecked
                  ? 'border-white/20 bg-white/[0.04]'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
              }`}
            >
              <div
                className={`w-[18px] h-[18px] sm:w-5 sm:h-5 rounded-[4px] ${
                  isWellness ? '' : 'mt-0.5'
                } shrink-0 flex items-center justify-center transition-colors border ${
                  requiredChecked
                    ? 'bg-white border-white text-black'
                    : 'bg-transparent border-[#64748B]'
                }`}
              >
                {requiredChecked && (
                  <FiCheck size={14} className="text-black stroke-[3.5]" />
                )}
              </div>
              <div className="flex flex-col gap-0.5 flex-1">
                <div className="text-xs font-semibold leading-snug text-slate-100">
                  {requiredLabel}
                </div>
                {!isWellness && (
                  <div className="text-[11px] text-[#98A2B3]">
                    {CONSENT_CONFIG.REQUIRED}
                  </div>
                )}
              </div>
            </div>

            {/* Checkbox 2: Optional (Eros Create only) */}
            {hasOptionalCheckbox && (
              <div
                onClick={() => setOptionalChecked(!optionalChecked)}
                className={`rounded-2xl p-3 sm:p-3.5 md:p-4 flex items-start gap-3 cursor-pointer transition-all border shrink-0 ${
                  optionalChecked
                    ? 'border-white/20 bg-white/[0.04]'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                }`}
              >
                <div
                  className={`w-[18px] h-[18px] sm:w-5 sm:h-5 rounded-[4px] mt-0.5 shrink-0 flex items-center justify-center transition-colors border ${
                    optionalChecked
                      ? 'bg-white border-white text-black'
                      : 'bg-transparent border-[#64748B]'
                  }`}
                >
                  {optionalChecked && (
                    <FiCheck size={14} className="text-black stroke-[3.5]" />
                  )}
                </div>
                <div className="flex flex-col gap-0.5">
                  <div className="text-xs font-semibold leading-snug text-slate-100">
                    {optionalLabel}
                  </div>
                  <div className="text-[11px] text-[#98A2B3]">
                    {CONSENT_CONFIG.OPTIONAL}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Action Button & Subtext */}
        <div className="shrink-0 flex flex-col gap-2 pt-1 pb-1">
          {isDetailsError && !details ? (
            <button
              type="button"
              onClick={handleClose}
              style={{ minHeight: '48px', height: '48px', flexShrink: 0 }}
              className="w-full h-12 min-h-[48px] shrink-0 rounded-xl text-sm font-bold border-0 transition-all flex items-center justify-center bg-[#1D2939] text-white cursor-pointer hover:bg-white/10"
            >
              Close
            </button>
          ) : isConsentLoading ? (
            <ShimmerEffect width="100%" height="48px" radius="12px" />
          ) : (
            <button
              type="button"
              disabled={isAgreeDisabled}
              onClick={handleAgreeAndContinue}
              style={{ minHeight: '48px', height: '48px', flexShrink: 0 }}
              className={`w-full h-12 min-h-[48px] shrink-0 rounded-xl text-sm font-bold border-0 transition-all flex items-center justify-center ${
                isAgreeDisabled
                  ? 'bg-[#1D2939] text-[#667085] cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#63E6A2] to-[#2ED3B7] text-[#0C111D] cursor-pointer hover:opacity-95 shadow-[0_4px_14px_rgba(46,211,183,0.3)]'
              }`}
            >
              {CONSENT_CONFIG.AGREE_AND_CONTINUE}
            </button>
          )}

          {!isConsentLoading && !isDetailsError && (
            isWellness ? (
              <div className="text-center text-[11px] leading-relaxed text-[#98A2B3] px-1 shrink-0">
                {CONSENT_CONFIG.WELLNESS_FOOTER_NOTE}
              </div>
            ) : (
              <div className="text-center text-[11px] font-medium text-[#98A2B3] shrink-0">
                {withdrawContent || CONSENT_CONFIG.CREATE_FOOTER_NOTE}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )
}

MiniAppConsentModal.propTypes = {
  isOpen: PropTypes.bool,
  miniAppId: PropTypes.string,
  isGuestUser: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  guestUser: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isModalBlocking: PropTypes.bool,
  onClose: PropTypes.func,
  onAgreed: PropTypes.func,
  onDeclined: PropTypes.func
}

export default MiniAppConsentModal
